import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Typography,
  Box,
  Stack,
  Avatar,
  Divider,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useSelector } from "react-redux";

const CashoutPaymentModal = ({ open, onClose, onConfirm }) => {
  const { data: paymentMethods, loading: paymentsLoading } = useSelector(
    (state) => state.paymentmethod
  );
  const { loading } = useSelector((u) => u?.cash);

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [accountValue, setAccountValue] = useState("");
  const [error, setError] = useState("");

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedMethod(null);
      setAccountValue("");
      setError("");
    } else if (paymentMethods.length > 0) {
      setSelectedMethod(paymentMethods[0]);
    }
  }, [open, paymentMethods]);

  const validateInput = () => {
    if (!accountValue.trim()) {
      setError("This field is required.");
      return false;
    } else if (accountValue.trim().length > 50) {
      setError("Payment details cannot exceed 50 characters.");
      return false;
    }
    setError("");
    return true;
  };

  const handleConfirm = () => {
    if (!selectedMethod) return;
    const isValid = validateInput();
    if (!isValid) return;
    onConfirm({
      method: selectedMethod?.type,
      accountValue: accountValue.trim(),
    });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setAccountValue(value);
    if (error) validateInput();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontWeight: 500,
          textAlign: "left",
          color: "text.primary",
        }}
      >
        Withdraw Funds
      </DialogTitle>
      <Divider />

      <DialogContent>
        {/* Payment Method Select */}
        <TextField
          select
          fullWidth
          label="Select Payment Method"
          value={selectedMethod?.type || ""}
          onChange={(e) => {
            const method = paymentMethods.find(
              (pm) => pm.type === e.target.value
            );
            setSelectedMethod(method);
            setAccountValue("");
            setError("");
          }}
          disabled={paymentsLoading}
          sx={{
            mt: 2,
            "& .MuiInputBase-input": { color: "text.primary" },
            "& .MuiInputLabel-root": { color: "text.primary" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          }}
        >
          {paymentsLoading ? (
            <MenuItem disabled>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading payment methods...
                </Typography>
              </Stack>
            </MenuItem>
          ) : paymentMethods.length > 0 ? (
            paymentMethods.map((pm) => (
              <MenuItem key={pm.type} value={pm.type}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    src={pm.icon}
                    alt={pm.type}
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      "& img": {
                        objectFit: "contain",
                      },
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      textTransform: "capitalize",
                    }}
                  >
                    {pm.type}
                  </Typography>
                </Stack>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No payment methods available</MenuItem>
          )}
        </TextField>

        {/* Dynamic Input Field */}
        {selectedMethod && (
          <Paper
            variant="outlined"
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                mb: 1,
                textTransform: "capitalize",
              }}
            >
              {selectedMethod.type} Withdrawal Details
            </Typography>

            <TextField
              fullWidth
              required
              label={`Enter your ${selectedMethod.type} details`}
              value={accountValue}
              onChange={handleChange}
              placeholder={`Enter your ${selectedMethod.type} details`}
              error={!!error}
              helperText={error}
              inputProps={{ maxLength: 50 }}
              sx={{
                mt: 1,
                "& .MuiInputBase-input": { color: "text.primary" },
                "& .MuiInputLabel-root": { color: "text.secondary" },
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1.5, display: "block" }}
            >
              Please ensure your {selectedMethod.type} details are correct
              before confirming the withdrawal.
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          fullWidth
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          fullWidth
          disabled={loading || !selectedMethod}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashoutPaymentModal;
